import { Stack, Typography, useTheme } from "@mui/material";
import { Error24Px } from "@qubit/autoparts";

import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import { formatLocalizedListParts } from "~/lib/helpers";
import { selectLocale } from "~/redux/selectors/siteSelectors";

import { CantInductReasonsMap } from "./autostorePutaway.types";

type CantInductAlertProps = {
  cantInductReasonsMap: CantInductReasonsMap;
};

const CantInductAlert = ({ cantInductReasonsMap }: CantInductAlertProps) => {
  const { palette } = useTheme();
  const { t } = useTranslation();
  const locale = useAppSelector(selectLocale);
  const reasonCodes = Object.keys(cantInductReasonsMap);

  if (reasonCodes.length === 0) {
    return null;
  }

  const reasonConjunctionParts = formatLocalizedListParts(reasonCodes, locale);

  return (
    <Stack
      direction="row"
      spacing={1.5}
      justifyContent="center"
      alignItems="center"
      role="alert"
      aria-label="cannot induct reasons banner"
    >
      <Error24Px
        fill={palette.error.main}
        style={{
          height: "30px",
          width: "30px"
        }}
      />
      <Typography
        sx={{
          whiteSpace: "pre-line",
          fontSize: "1.25rem",
          fontWeight: "normal"
        }}
      >
        {t("cannot induct product reasons")}
        {reasonConjunctionParts.map((part, partIndex) => {
          if (part.type === "literal") {
            return <span key={`literal-${partIndex}`}>{part.value}</span>;
          }

          const reasonCode = part.value;
          const reasonEntry = cantInductReasonsMap[reasonCode];
          // Keys come from this same map
          // Entry is only undefined due to Partial<> typing
          if (!reasonEntry) {
            return null;
          }

          const {
            translationReasonKey,
            originalReasonCode,
            additionalDetails = []
          } = reasonEntry;
          const detailConjunctionParts = formatLocalizedListParts(
            additionalDetails.map((detail) =>
              detail.translationKey ? t(detail.translationKey) : detail.original
            ),
            locale
          );

          return (
            <span
              style={{
                fontWeight: "bold"
              }}
              key={`reason-${partIndex}`}
            >
              {translationReasonKey
                ? t(translationReasonKey)
                : originalReasonCode}

              {detailConjunctionParts.length > 0 && (
                <span>
                  {" ("}
                  {detailConjunctionParts.map((detailPart, detailPartIndex) => (
                    <span key={`detail-${partIndex}-${detailPartIndex}`}>
                      {detailPart.value}
                    </span>
                  ))}
                  {")"}
                </span>
              )}
            </span>
          );
        })}
        {"."}
      </Typography>
    </Stack>
  );
};

export default CantInductAlert;
